import { Texture, type TextStyleOptions } from 'pixi.js';

/**
 * The shared look of the game's own screens, drawn in Pixi: type, colours
 * and small helpers every board, dialog and game uses (producer 2026-10-14:
 * painted art, brass and wood, no purple, no plain boxes).
 */

/** Display type: chunky titles and numbers. */
export const DISPLAY_FONT = '"Lilita One", "Fredoka", system-ui, sans-serif';
/** UI type: everything else. */
export const UI_FONT = '"Fredoka", system-ui, -apple-system, sans-serif';

export const COLOURS = {
  /** The brass edge of every board and button. */
  brass: 0xc8963a,
  brassLight: 0xffdc8c,
  /** Text outlines and the darkest wood. */
  ink: 0x2a1605,
  /** Body text on wood. */
  cream: 0xffe8b0,
  /** Titles and numbers. */
  gold: 0xffe48a,
  woodLight: 0x6b3f17,
  woodDark: 0x3f230b,
  /** Under a board's floor art (and in its place before it loads). */
  floor: 0x1f1608,
  loss: 0xff9c8a,
} as const;

/** Gold display type with a dark outline, readable on the forest floor. */
export function displayStyle(size: number, fill: number = COLOURS.gold): TextStyleOptions {
  return {
    fontFamily: DISPLAY_FONT,
    fontSize: size,
    fill,
    letterSpacing: Math.round(size * 0.05),
    stroke: { color: COLOURS.ink, width: Math.max(2, Math.round(size * 0.14)), join: 'round' },
    dropShadow: { color: 0x000000, alpha: 0.55, blur: 3, distance: 2, angle: Math.PI / 2 },
  };
}

/** Body type on wood. */
export function uiStyle(size: number, fill: number = COLOURS.cream, weight: '500' | '600' | '700' = '700'): TextStyleOptions {
  return {
    fontFamily: UI_FONT,
    fontSize: size,
    fontWeight: weight,
    fill,
    stroke: { color: COLOURS.ink, width: Math.max(2, Math.round(size * 0.18)), join: 'round' },
  };
}

let fontsLoaded: Promise<void> | null = null;

/**
 * Wait for the game's two fonts (index.html loads them), at most
 * `timeoutMs`: Pixi draws text once, so text drawn before they arrive keeps
 * the fallback. Never rejects.
 */
export function loadUiFonts(timeoutMs = 2500): Promise<void> {
  if (fontsLoaded) return fontsLoaded;
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
  if (!fonts?.load) return (fontsLoaded = Promise.resolve());
  const load = Promise.all([
    fonts.load('400 32px "Lilita One"'),
    fonts.load('700 16px "Fredoka"'),
    fonts.load('500 16px "Fredoka"'),
  ]).then(() => undefined, () => undefined);
  fontsLoaded = Promise.race([load, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
  return fontsLoaded;
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * Run `step(t)` for t from 0 to 1 over `ms` on animation frames. A step that
 * throws stops the animation there and resolves it: what it moved was
 * destroyed under it (its scene went, say, mid-close), and whatever awaits
 * it carries on rather than waiting for ever.
 */
export function animate(ms: number, step: (t: number) => void): Promise<void> {
  return new Promise((resolve) => {
    const run = (t: number): boolean => {
      try {
        step(t);
        return true;
      } catch (err) {
        console.warn('[animate] stopped: a step threw', err);
        return false;
      }
    };
    if (ms <= 0 || prefersReducedMotion()) {
      run(1);
      resolve();
      return;
    }
    const start = performance.now();
    const frame = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      if (run(t) && t < 1) requestAnimationFrame(frame);
      else resolve();
    };
    requestAnimationFrame(frame);
  });
}

export const ease = {
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  inCubic: (t: number) => t * t * t,
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
};

const fades = new Map<string, Texture>();

/**
 * A horizontal alpha ramp for masks: opaque in the middle, fading over
 * `fadeIn` of the width on the left and `fadeOut` on the right (0 to 1).
 */
export function fadeTexture(fadeIn: number, fadeOut: number): Texture {
  const key = `${fadeIn.toFixed(3)}:${fadeOut.toFixed(3)}`;
  const hit = fades.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 4;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createLinearGradient(0, 0, 256, 0);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(Math.max(0.001, fadeIn), 'rgba(255,255,255,1)');
    g.addColorStop(Math.min(0.999, 1 - fadeOut), 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 4);
  }
  const texture = Texture.from(canvas);
  fades.set(key, texture);
  return texture;
}
